/**
 * U14 F4: template oficial do WhatsApp (modelo de mensagem da Meta), igual na tela e no servidor.
 *
 * - **Definição:** o que o editor de Comercial > Conversas > Modelos de mensagem preenche (categoria, cabeçalho, corpo,
 *   rodapé, botões e o exemplo de cada variável).
 * - **Validação:** a mesma regra no editor (problemas na tela) e no servidor (salvar e mandar para aprovação recusam).
 * - **Componentes:** a definição no formato da Meta (`components` do `POST /{waba}/message_templates`, o mesmo que a Zernio
 *   repassa) e o caminho de volta (editar um template sincronizado).
 * - **Envio:** o que o template pede na hora de mandar (variáveis do corpo e do cabeçalho, parâmetros dos botões).
 *
 * Sem dependência externa (o pacote é usado pelo web e pelos módulos).
 */

export const CATEGORIAS_TEMPLATE = ['MARKETING', 'UTILITY', 'AUTHENTICATION'] as const;
export type CategoriaTemplate = (typeof CATEGORIAS_TEMPLATE)[number];
export const TIPOS_CABECALHO = ['nenhum', 'texto', 'imagem', 'video', 'documento'] as const;
export type TipoCabecalho = (typeof TIPOS_CABECALHO)[number];
export const TIPOS_BOTAO_TEMPLATE = ['resposta', 'link', 'telefone', 'copiar'] as const;
export type TipoBotaoTemplate = (typeof TIPOS_BOTAO_TEMPLATE)[number];

/** Limites da Meta (documentação de templates da WhatsApp Cloud API). */
export const LIMITES_TEMPLATE = {
  nome: 512,
  cabecalho: 60,
  corpo: 1024,
  rodape: 60,
  botoes: 10,
  rotulo: 25,
  respostas: 10,
  links: 2,
  telefones: 1,
  copiar: 1,
  url: 2000,
  /** Exemplo de variável (corpo, cabeçalho e final de link). */
  exemplo: 200,
  /** Código do botão "copiar código". */
  codigo: 15,
  /** Validade do código do template de autenticação, em minutos. */
  expiracao: 90,
} as const;

/** Mídia aceita como exemplo do cabeçalho (o arquivo de amostra que vai à Meta na aprovação). */
export const MIME_CABECALHO: Record<'imagem' | 'video' | 'documento', readonly string[]> = {
  imagem: ['image/jpeg', 'image/png'],
  video: ['video/mp4'],
  documento: ['application/pdf'],
};

export interface BotaoTemplate {
  tipo: TipoBotaoTemplate;
  /** Rótulo do botão (o "copiar código" usa o rótulo padrão da Meta). */
  rotulo: string;
  /** Link: endereço https; com `{{1}}` no fim, o final muda a cada envio. */
  url?: string;
  /** Telefone com DDI. */
  telefone?: string;
  /** Exemplo do final do link (com variável) ou do código do "copiar código". */
  exemplo?: string;
}

export interface CabecalhoTemplate {
  tipo: TipoCabecalho;
  /** Texto do cabeçalho (até 60, no máximo uma variável). */
  texto?: string;
  /** Exemplo da variável do cabeçalho de texto. */
  exemplo?: string;
  /** Cabeçalho de mídia: o arquivo de amostra (anexo do template), obrigatório para a aprovação. */
  anexo_id?: string | null;
}

export interface AutenticacaoTemplate {
  /** Aviso de segurança da Meta ("Não compartilhe este código"). */
  seguranca: boolean;
  /** Validade do código, em minutos (rodapé da Meta); nulo, sem aviso. */
  expiracao_minutos: number | null;
}

export interface DefinicaoTemplate {
  nome: string;
  idioma: string;
  categoria: CategoriaTemplate;
  cabecalho: CabecalhoTemplate;
  corpo: string;
  /** Exemplo de cada variável do corpo, pela chave dela (`1`, `2`... ou o nome). */
  exemplos: Record<string, string>;
  rodape: string;
  botoes: BotaoTemplate[];
  /** Só na categoria AUTHENTICATION: o corpo é o padrão da Meta; muda só o aviso e a validade. */
  autenticacao?: AutenticacaoTemplate;
}

export interface ProblemaTemplate {
  /** Campo com o problema (`corpo`, `cabecalho`, `rodape`, `botoes.2`, `exemplos.nome`...). */
  campo: string;
  /** Código do erro (`template.<codigo>`), traduzido na tela pelo dicionário de erros. */
  codigo: string;
  mensagem: string;
}

export type FormatoParametros = 'posicional' | 'nomeado';

const RE_VARIAVEL = /\{\{([^{}]*)\}\}/g;
const RE_POSICIONAL = /^[1-9]\d{0,2}$/;
/** Nome de variável da Meta: minúsculas, números e sublinhado, começando por letra. */
const RE_NOMEADA = /^[a-z][a-z0-9_]{0,59}$/;
const RE_NOME = /^[a-z][a-z0-9_]*$/;
const RE_IDIOMA = /^[a-z]{2,3}(_[A-Z]{2})?$/;
const RE_TELEFONE = /^\+?[1-9]\d{7,19}$/;
const RE_CODIGO = /^[A-Za-z0-9]+$/;
const RE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Corpo do template de autenticação (a Meta usa o texto padrão dela no idioma do template). */
export const CORPO_AUTENTICACAO = '{{1}} é o seu código de verificação.';

/** Variáveis do texto na ordem em que aparecem (repetidas uma vez só); `invalidas`: `{{ }}` fora do formato. */
export function variaveisDoTexto(texto: string): { nomes: string[]; invalidas: string[]; formato: FormatoParametros | 'misturado' | null } {
  const nomes: string[] = [];
  const invalidas: string[] = [];
  let pos = false;
  let nom = false;
  for (const m of texto.matchAll(RE_VARIAVEL)) {
    const v = m[1]!.trim();
    if (RE_POSICIONAL.test(v)) pos = true;
    else if (RE_NOMEADA.test(v)) nom = true;
    else {
      invalidas.push(m[0]);
      continue;
    }
    if (!nomes.includes(v)) nomes.push(v);
  }
  return { nomes, invalidas, formato: pos && nom ? 'misturado' : pos ? 'posicional' : nom ? 'nomeado' : null };
}

/** As variáveis do corpo na ordem de envio: posicionais pelo número (`{{1}}`, `{{2}}`...), nomeadas pela primeira aparição. */
export function ordemDeEnvio(nomes: readonly string[]): string[] {
  return nomes.every((n) => RE_POSICIONAL.test(n)) ? [...nomes].sort((a, b) => Number(a) - Number(b)) : [...nomes];
}

/** Formato dos parâmetros do template inteiro (corpo e cabeçalho); sem variável, posicional. */
export function formatoDoTemplate(d: Pick<DefinicaoTemplate, 'corpo' | 'cabecalho'>): FormatoParametros {
  const corpo = variaveisDoTexto(d.corpo).formato;
  const cab = d.cabecalho.tipo === 'texto' ? variaveisDoTexto(d.cabecalho.texto ?? '').formato : null;
  return corpo === 'nomeado' || cab === 'nomeado' ? 'nomeado' : 'posicional';
}

/** Troca as variáveis do texto pelos valores (pela chave); a que não tem valor fica como está. */
export function preencherVariaveis(texto: string, valores: Record<string, string | undefined>): string {
  return texto.replace(RE_VARIAVEL, (bruto, v: string) => {
    const valor = valores[v.trim()];
    return valor?.trim() ? valor : bruto;
  });
}

const vazio = (v: unknown) => typeof v !== 'string' || !v.trim();
const linha = (v: string) => /[\r\n]/.test(v);

function problemasDeVariaveis(texto: string, campo: string, exemplos: Record<string, string>, p: ProblemaTemplate[], maximo?: number) {
  const v = variaveisDoTexto(texto);
  if (v.invalidas.length) p.push({ campo, codigo: 'template.variavel_invalida', mensagem: `Variável fora do formato: ${v.invalidas[0]} (use {{1}} ou {{nome}}, com minúsculas, números e _)` });
  if (v.formato === 'misturado') p.push({ campo, codigo: 'template.variaveis_misturadas', mensagem: 'Use só números ({{1}}) ou só nomes ({{nome}}) nas variáveis' });
  if (v.formato === 'posicional') {
    const nums = v.nomes.map(Number).sort((a, b) => a - b);
    if (nums.some((n, i) => n !== i + 1)) p.push({ campo, codigo: 'template.variaveis_sequencia', mensagem: 'As variáveis numeradas começam em {{1}} e seguem sem pular número' });
  }
  if (maximo !== undefined && v.nomes.length > maximo) p.push({ campo, codigo: 'template.variaveis_demais', mensagem: `No máximo ${maximo} variável neste campo` });
  for (const n of v.nomes) {
    const ex = exemplos[n];
    if (vazio(ex)) p.push({ campo: `exemplos.${n}`, codigo: 'template.exemplo_obrigatorio', mensagem: `Informe um exemplo para a variável {{${n}}}` });
    else if (ex!.length > LIMITES_TEMPLATE.exemplo || linha(ex!)) p.push({ campo: `exemplos.${n}`, codigo: 'template.exemplo_tamanho', mensagem: `O exemplo de {{${n}}} tem uma linha de até ${LIMITES_TEMPLATE.exemplo} caracteres` });
  }
  return v;
}

/** Problemas da definição (vazio = pode salvar e mandar para aprovação). A mesma regra na tela e no servidor. */
export function validarTemplate(d: DefinicaoTemplate): ProblemaTemplate[] {
  const p: ProblemaTemplate[] = [];
  if (vazio(d.nome) || !RE_NOME.test(d.nome) || d.nome.length > LIMITES_TEMPLATE.nome) {
    p.push({ campo: 'nome', codigo: 'template.nome', mensagem: 'Nome com letras minúsculas, números e _, começando por letra' });
  }
  if (!RE_IDIOMA.test(d.idioma ?? '')) p.push({ campo: 'idioma', codigo: 'template.idioma', mensagem: 'Idioma no formato da Meta (pt_BR, en_US, es)' });
  if (!CATEGORIAS_TEMPLATE.includes(d.categoria)) p.push({ campo: 'categoria', codigo: 'template.categoria', mensagem: 'Escolha a categoria' });

  if (d.categoria === 'AUTHENTICATION') {
    const a = d.autenticacao;
    const exp = a?.expiracao_minutos;
    if (exp !== null && exp !== undefined && (!Number.isInteger(exp) || exp < 1 || exp > LIMITES_TEMPLATE.expiracao)) {
      p.push({ campo: 'autenticacao.expiracao_minutos', codigo: 'template.expiracao', mensagem: `Validade do código de 1 a ${LIMITES_TEMPLATE.expiracao} minutos` });
    }
    if (d.cabecalho.tipo !== 'nenhum') p.push({ campo: 'cabecalho', codigo: 'template.autenticacao_cabecalho', mensagem: 'Template de autenticação não tem cabeçalho' });
    if (d.botoes.length !== 1 || d.botoes[0]!.tipo !== 'copiar') p.push({ campo: 'botoes', codigo: 'template.autenticacao_botao', mensagem: 'Template de autenticação tem só o botão "Copiar código"' });
    else if (d.botoes[0]!.rotulo.length > LIMITES_TEMPLATE.rotulo) p.push({ campo: 'botoes.0', codigo: 'template.rotulo', mensagem: `Rótulo do botão de até ${LIMITES_TEMPLATE.rotulo} caracteres` });
    return p;
  }

  // Cabeçalho.
  const c = d.cabecalho;
  if (!TIPOS_CABECALHO.includes(c.tipo)) p.push({ campo: 'cabecalho', codigo: 'template.cabecalho_tipo', mensagem: 'Tipo de cabeçalho inválido' });
  else if (c.tipo === 'texto') {
    const t = c.texto ?? '';
    if (vazio(t)) p.push({ campo: 'cabecalho', codigo: 'template.cabecalho_vazio', mensagem: 'Informe o texto do cabeçalho' });
    else if (t.length > LIMITES_TEMPLATE.cabecalho) p.push({ campo: 'cabecalho', codigo: 'template.cabecalho_tamanho', mensagem: `Cabeçalho de até ${LIMITES_TEMPLATE.cabecalho} caracteres` });
    else if (linha(t)) p.push({ campo: 'cabecalho', codigo: 'template.cabecalho_linha', mensagem: 'O cabeçalho tem uma linha só' });
    const vars = variaveisDoTexto(t);
    problemasDeVariaveis(t, 'cabecalho', vars.nomes.length ? { [vars.nomes[0]!]: c.exemplo ?? '' } : {}, p, 1);
  } else if (c.tipo !== 'nenhum' && !(typeof c.anexo_id === 'string' && RE_UUID.test(c.anexo_id))) {
    p.push({ campo: 'cabecalho', codigo: 'template.cabecalho_amostra', mensagem: 'Envie um arquivo de amostra para o cabeçalho (a Meta pede na aprovação)' });
  }

  // Corpo.
  if (vazio(d.corpo)) p.push({ campo: 'corpo', codigo: 'template.corpo_vazio', mensagem: 'Informe o texto do corpo' });
  else if (d.corpo.length > LIMITES_TEMPLATE.corpo) p.push({ campo: 'corpo', codigo: 'template.corpo_tamanho', mensagem: `Corpo de até ${LIMITES_TEMPLATE.corpo} caracteres` });
  else {
    const v = problemasDeVariaveis(d.corpo, 'corpo', d.exemplos ?? {}, p);
    const limpo = d.corpo.trim();
    if (v.nomes.length && (/^\{\{[^{}]*\}\}/.test(limpo) || /\{\{[^{}]*\}\}$/.test(limpo))) {
      p.push({ campo: 'corpo', codigo: 'template.variavel_borda', mensagem: 'O corpo não pode começar nem terminar com uma variável' });
    }
  }
  // Mesmo formato no template inteiro (a Meta pede um `parameter_format` só).
  if (c.tipo === 'texto') {
    const fc = variaveisDoTexto(c.texto ?? '').formato;
    const fb = variaveisDoTexto(d.corpo ?? '').formato;
    if (fc && fb && fc !== 'misturado' && fb !== 'misturado' && fc !== fb) p.push({ campo: 'cabecalho', codigo: 'template.variaveis_misturadas', mensagem: 'Use só números ({{1}}) ou só nomes ({{nome}}) nas variáveis' });
  }

  // Rodapé.
  if (d.rodape) {
    if (d.rodape.length > LIMITES_TEMPLATE.rodape) p.push({ campo: 'rodape', codigo: 'template.rodape_tamanho', mensagem: `Rodapé de até ${LIMITES_TEMPLATE.rodape} caracteres` });
    else if (/\{\{/.test(d.rodape)) p.push({ campo: 'rodape', codigo: 'template.rodape_variavel', mensagem: 'O rodapé não leva variável' });
    else if (linha(d.rodape)) p.push({ campo: 'rodape', codigo: 'template.rodape_linha', mensagem: 'O rodapé tem uma linha só' });
  }

  // Botões.
  const b = d.botoes ?? [];
  if (b.length > LIMITES_TEMPLATE.botoes) p.push({ campo: 'botoes', codigo: 'template.botoes_demais', mensagem: `No máximo ${LIMITES_TEMPLATE.botoes} botões` });
  const conta = (t: TipoBotaoTemplate) => b.filter((x) => x.tipo === t).length;
  if (conta('resposta') > LIMITES_TEMPLATE.respostas) p.push({ campo: 'botoes', codigo: 'template.respostas_demais', mensagem: `No máximo ${LIMITES_TEMPLATE.respostas} respostas rápidas` });
  if (conta('link') > LIMITES_TEMPLATE.links) p.push({ campo: 'botoes', codigo: 'template.links_demais', mensagem: `No máximo ${LIMITES_TEMPLATE.links} botões de link` });
  if (conta('telefone') > LIMITES_TEMPLATE.telefones) p.push({ campo: 'botoes', codigo: 'template.telefones_demais', mensagem: 'No máximo 1 botão de telefone' });
  if (conta('copiar') > LIMITES_TEMPLATE.copiar) p.push({ campo: 'botoes', codigo: 'template.copiar_demais', mensagem: 'No máximo 1 botão "Copiar código"' });
  // A Meta pede as respostas rápidas juntas (antes ou depois dos outros botões, sem intercalar).
  const tiposRuns = b.map((x) => (x.tipo === 'resposta' ? 'r' : 'o')).join('').replace(/(.)\1+/g, '$1');
  if (tiposRuns.length > 2) p.push({ campo: 'botoes', codigo: 'template.botoes_agrupados', mensagem: 'Deixe as respostas rápidas juntas, antes ou depois dos outros botões' });
  const rotulos = new Set<string>();
  b.forEach((x, i) => {
    const campo = `botoes.${i}`;
    if (!TIPOS_BOTAO_TEMPLATE.includes(x.tipo)) return void p.push({ campo, codigo: 'template.botao_tipo', mensagem: 'Tipo de botão inválido' });
    if (x.tipo !== 'copiar') {
      if (vazio(x.rotulo) || x.rotulo.length > LIMITES_TEMPLATE.rotulo) p.push({ campo, codigo: 'template.rotulo', mensagem: `Rótulo do botão de 1 a ${LIMITES_TEMPLATE.rotulo} caracteres` });
      else if (/\{\{/.test(x.rotulo)) p.push({ campo, codigo: 'template.rotulo_variavel', mensagem: 'O rótulo do botão não leva variável' });
      else if (rotulos.has(x.rotulo.trim().toLowerCase())) p.push({ campo, codigo: 'template.rotulo_repetido', mensagem: 'Dois botões com o mesmo rótulo' });
      else rotulos.add(x.rotulo.trim().toLowerCase());
    }
    if (x.tipo === 'link') {
      const url = x.url ?? '';
      const vars = variaveisDoTexto(url);
      if (!/^https:\/\/[^\s{}]+(\{\{1\}\})?$/.test(url) || url.length > LIMITES_TEMPLATE.url || vars.nomes.length > 1 || (vars.nomes.length === 1 && vars.nomes[0] !== '1')) {
        p.push({ campo, codigo: 'template.url', mensagem: 'Link https://, com no máximo {{1}} no fim' });
      } else if (vars.nomes.length && (vazio(x.exemplo) || x.exemplo!.length > LIMITES_TEMPLATE.exemplo || /\s/.test(x.exemplo!))) {
        p.push({ campo, codigo: 'template.exemplo_url', mensagem: 'Informe um exemplo do final do link, sem espaço' });
      }
    }
    if (x.tipo === 'telefone' && !RE_TELEFONE.test((x.telefone ?? '').replace(/[\s()-]/g, ''))) p.push({ campo, codigo: 'template.telefone', mensagem: 'Telefone com DDI, só números (ex.: +5511999999999)' });
    if (x.tipo === 'copiar' && (vazio(x.exemplo) || x.exemplo!.length > LIMITES_TEMPLATE.codigo || !RE_CODIGO.test(x.exemplo!))) {
      p.push({ campo, codigo: 'template.codigo', mensagem: `Exemplo do código com letras e números, até ${LIMITES_TEMPLATE.codigo}` });
    }
  });
  return p;
}

// ---------------------------------------------------------------------------
// Formato da Meta
// ---------------------------------------------------------------------------

export interface ComponenteMeta {
  type: string;
  format?: string;
  text?: string;
  example?: Record<string, unknown>;
  buttons?: Record<string, unknown>[];
  add_security_recommendation?: boolean;
  code_expiration_minutes?: number;
}

export interface CriacaoTemplateMeta {
  name: string;
  language: string;
  category: CategoriaTemplate;
  parameter_format: 'POSITIONAL' | 'NAMED';
  components: ComponenteMeta[];
}

const FORMATO_MIDIA = { imagem: 'IMAGE', video: 'VIDEO', documento: 'DOCUMENT' } as const;

function exemploDoTexto(texto: string, exemplos: Record<string, string>, chave: 'header' | 'body'): Record<string, unknown> | undefined {
  const v = variaveisDoTexto(texto);
  if (!v.nomes.length) return undefined;
  if (v.formato === 'nomeado') {
    return { [`${chave}_text_named_params`]: v.nomes.map((n) => ({ param_name: n, example: (exemplos[n] ?? '').trim() })) };
  }
  const valores = ordemDeEnvio(v.nomes).map((n) => (exemplos[n] ?? '').trim());
  return chave === 'header' ? { header_text: valores } : { body_text: [valores] };
}

/**
 * A definição no formato da Meta (corpo do `POST /{waba}/message_templates`). `amostraCabecalho`: o identificador da mídia de
 * amostra já enviada à Meta (`header_handle`), só no cabeçalho de mídia; sem ela, o componente sai sem exemplo (rascunho).
 */
export function componentesDoTemplate(d: DefinicaoTemplate, amostraCabecalho?: string | null): CriacaoTemplateMeta {
  const comp: ComponenteMeta[] = [];
  if (d.categoria === 'AUTHENTICATION') {
    comp.push({ type: 'BODY', add_security_recommendation: !!d.autenticacao?.seguranca });
    if (d.autenticacao?.expiracao_minutos) comp.push({ type: 'FOOTER', code_expiration_minutes: d.autenticacao.expiracao_minutos });
    const rotulo = d.botoes[0]?.rotulo?.trim();
    comp.push({ type: 'BUTTONS', buttons: [{ type: 'OTP', otp_type: 'COPY_CODE', ...(rotulo ? { text: rotulo } : {}) }] });
    return { name: d.nome, language: d.idioma, category: d.categoria, parameter_format: 'POSITIONAL', components: comp };
  }
  const c = d.cabecalho;
  if (c.tipo === 'texto') {
    const vars = variaveisDoTexto(c.texto ?? '');
    const ex = exemploDoTexto(c.texto ?? '', vars.nomes.length ? { [vars.nomes[0]!]: c.exemplo ?? '' } : {}, 'header');
    comp.push({ type: 'HEADER', format: 'TEXT', text: c.texto ?? '', ...(ex ? { example: ex } : {}) });
  } else if (c.tipo !== 'nenhum') {
    comp.push({ type: 'HEADER', format: FORMATO_MIDIA[c.tipo], ...(amostraCabecalho ? { example: { header_handle: [amostraCabecalho] } } : {}) });
  }
  const exCorpo = exemploDoTexto(d.corpo, d.exemplos ?? {}, 'body');
  comp.push({ type: 'BODY', text: d.corpo, ...(exCorpo ? { example: exCorpo } : {}) });
  if (d.rodape?.trim()) comp.push({ type: 'FOOTER', text: d.rodape });
  if (d.botoes?.length) {
    comp.push({
      type: 'BUTTONS',
      buttons: d.botoes.map((b) => {
        if (b.tipo === 'resposta') return { type: 'QUICK_REPLY', text: b.rotulo };
        if (b.tipo === 'telefone') return { type: 'PHONE_NUMBER', text: b.rotulo, phone_number: (b.telefone ?? '').replace(/[\s()-]/g, '') };
        if (b.tipo === 'copiar') return { type: 'COPY_CODE', example: (b.exemplo ?? '').trim() };
        const url = b.url ?? '';
        return { type: 'URL', text: b.rotulo, url, ...(url.includes('{{1}}') ? { example: [url.replace('{{1}}', (b.exemplo ?? '').trim())] } : {}) };
      }),
    });
  }
  return { name: d.nome, language: d.idioma, category: d.categoria, parameter_format: formatoDoTemplate(d) === 'nomeado' ? 'NAMED' : 'POSITIONAL', components: comp };
}

type Obj = Record<string, unknown>;
const obj = (v: unknown): Obj => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : {});
const lista = (v: unknown): Obj[] => (Array.isArray(v) ? v.map(obj) : []);
const str = (v: unknown) => (typeof v === 'string' ? v : '');
const TIPO_DO_FORMATO: Record<string, TipoCabecalho> = { TEXT: 'texto', IMAGE: 'imagem', VIDEO: 'video', DOCUMENT: 'documento' };

/** Caminho de volta: os `components` da Meta (template sincronizado ou rascunho salvo) viram a definição do editor. */
export function definicaoDosComponentes(base: { nome: string; idioma: string; categoria: CategoriaTemplate; anexo_id?: string | null }, componentes: unknown): DefinicaoTemplate {
  const comps = lista(componentes);
  const achar = (t: string) => comps.find((c) => str(c.type).toUpperCase() === t);
  const header = achar('HEADER');
  const body = achar('BODY');
  const footer = achar('FOOTER');
  const botoesMeta = lista(achar('BUTTONS')?.buttons);
  const d: DefinicaoTemplate = { nome: base.nome, idioma: base.idioma, categoria: base.categoria, cabecalho: { tipo: 'nenhum' }, corpo: str(body?.text), exemplos: {}, rodape: str(footer?.text), botoes: [] };

  if (base.categoria === 'AUTHENTICATION') {
    const exp = Number(footer?.code_expiration_minutes);
    const otp = botoesMeta[0];
    return {
      ...d,
      corpo: d.corpo || CORPO_AUTENTICACAO,
      rodape: '',
      autenticacao: { seguranca: body?.add_security_recommendation === true, expiracao_minutos: Number.isInteger(exp) && exp > 0 ? exp : null },
      botoes: [{ tipo: 'copiar', rotulo: str(otp?.text), exemplo: 'CODIGO' }],
    };
  }

  if (header) {
    const tipo = TIPO_DO_FORMATO[str(header.format).toUpperCase()] ?? 'texto';
    if (tipo === 'texto') {
      const texto = str(header.text);
      const ex = obj(header.example);
      const nomeado = lista(ex.header_text_named_params)[0];
      const posicional = Array.isArray(ex.header_text) ? str(ex.header_text[0]) : '';
      d.cabecalho = { tipo, texto, exemplo: nomeado ? str(nomeado.example) : posicional };
    } else d.cabecalho = { tipo, anexo_id: base.anexo_id ?? null };
  }
  const ex = obj(body?.example);
  const nomeados = lista(ex.body_text_named_params);
  if (nomeados.length) for (const n of nomeados) d.exemplos[str(n.param_name)] = str(n.example);
  else {
    const valores = Array.isArray(ex.body_text) && Array.isArray(ex.body_text[0]) ? (ex.body_text[0] as unknown[]).map(str) : [];
    ordemDeEnvio(variaveisDoTexto(d.corpo).nomes).forEach((n, i) => (d.exemplos[n] = valores[i] ?? ''));
  }
  d.botoes = botoesMeta.map((b): BotaoTemplate => {
    const t = str(b.type).toUpperCase();
    if (t === 'URL') {
      const url = str(b.url);
      const exemplo = Array.isArray(b.example) ? str(b.example[0]) : '';
      const prefixo = url.split('{{1}}')[0] ?? '';
      return { tipo: 'link', rotulo: str(b.text), url, exemplo: url.includes('{{1}}') && exemplo.startsWith(prefixo) ? exemplo.slice(prefixo.length) : '' };
    }
    if (t === 'PHONE_NUMBER') return { tipo: 'telefone', rotulo: str(b.text), telefone: str(b.phone_number) };
    if (t === 'COPY_CODE') return { tipo: 'copiar', rotulo: '', exemplo: Array.isArray(b.example) ? str(b.example[0]) : str(b.example) };
    return { tipo: 'resposta', rotulo: str(b.text) };
  });
  return d;
}

// ---------------------------------------------------------------------------
// Envio
// ---------------------------------------------------------------------------

/** O que o template aprovado pede na hora do envio (atendimento, mensageria do motor e broadcast). */
export interface RequisitosEnvio {
  formato: FormatoParametros;
  /** Variáveis do corpo na ordem do envio (os valores chegam nessa ordem). */
  corpo: string[];
  cabecalho: { tipo: 'texto'; variaveis: string[] } | { tipo: 'imagem' | 'video' | 'documento' } | null;
  /** Botões que pedem parâmetro no envio: o final do link dinâmico e o código do "copiar código", pela posição. */
  botoes: { indice: number; tipo: 'link' | 'codigo' }[];
  /** Posições das respostas rápidas (o código do clique é opcional). */
  respostas: number[];
}

export function requisitosDeEnvio(componentes: unknown): RequisitosEnvio {
  const comps = lista(componentes);
  const achar = (t: string) => comps.find((c) => str(c.type).toUpperCase() === t);
  const header = achar('HEADER');
  const corpo = variaveisDoTexto(str(achar('BODY')?.text));
  let cabecalho: RequisitosEnvio['cabecalho'] = null;
  let formatoCab: string | null = null;
  if (header) {
    const tipo = TIPO_DO_FORMATO[str(header.format).toUpperCase()] ?? 'texto';
    if (tipo === 'texto') {
      const v = variaveisDoTexto(str(header.text));
      formatoCab = v.formato;
      cabecalho = v.nomes.length ? { tipo: 'texto', variaveis: v.nomes } : null;
    } else if (tipo !== 'nenhum') cabecalho = { tipo };
  }
  const botoes: RequisitosEnvio['botoes'] = [];
  const respostas: number[] = [];
  lista(achar('BUTTONS')?.buttons).forEach((b, i) => {
    const t = str(b.type).toUpperCase();
    if (t === 'URL' && str(b.url).includes('{{')) botoes.push({ indice: i, tipo: 'link' });
    else if (t === 'COPY_CODE') botoes.push({ indice: i, tipo: 'codigo' });
    else if (t === 'QUICK_REPLY') respostas.push(i);
  });
  return { formato: corpo.formato === 'nomeado' || formatoCab === 'nomeado' ? 'nomeado' : 'posicional', corpo: ordemDeEnvio(corpo.nomes), cabecalho, botoes, respostas };
}

/** Parâmetros do envio que dependem do template (o mesmo formato do `TemplateEnvio` do Conversas). */
export interface ParametrosEnvio {
  variaveis: string[];
  cabecalho?: { tipo: 'texto'; variaveis: string[] } | { tipo: 'imagem' | 'video' | 'documento'; link: string; nome?: string };
  botoes?: ({ indice: number; tipo: 'resposta'; payload: string } | { indice: number; tipo: 'link'; texto: string } | { indice: number; tipo: 'codigo'; codigo: string })[];
}

/**
 * Confere o envio contra o que o template aprovado pede (cabeçalho e botões com parâmetro). Devolve o problema em pt-BR, ou
 * null. O corpo é conferido à parte (`renderizarTemplate` do Conversas). Resposta rápida sem código é aceita (a Meta devolve
 * o rótulo no clique).
 */
export function conferirParametrosEnvio(req: RequisitosEnvio, p: ParametrosEnvio): { codigo: string; mensagem: string } | null {
  const c = req.cabecalho;
  if (c?.tipo === 'texto') {
    if (p.cabecalho?.tipo !== 'texto' || p.cabecalho.variaveis.length !== c.variaveis.length || p.cabecalho.variaveis.some((v) => !v.trim())) {
      return { codigo: 'template.cabecalho_variavel', mensagem: 'Informe a variável do cabeçalho do template' };
    }
  } else if (c) {
    if (!p.cabecalho || p.cabecalho.tipo !== c.tipo) return { codigo: 'template.cabecalho_midia', mensagem: 'Informe o link https da mídia do cabeçalho do template' };
  } else if (p.cabecalho) return { codigo: 'template.cabecalho_sobra', mensagem: 'O template não tem cabeçalho com parâmetro' };
  const dados = new Map((p.botoes ?? []).map((b) => [b.indice, b]));
  for (const b of req.botoes) {
    const dado = dados.get(b.indice);
    if (!dado || dado.tipo !== b.tipo) return { codigo: 'template.botao_parametro', mensagem: `Informe ${b.tipo === 'link' ? 'o final do link' : 'o código'} do botão ${b.indice + 1} do template` };
  }
  for (const b of p.botoes ?? []) {
    const pede = req.botoes.find((x) => x.indice === b.indice);
    if (b.tipo === 'resposta' ? !req.respostas.includes(b.indice) : !pede) return { codigo: 'template.botao_sobra', mensagem: `O botão ${b.indice + 1} do template não recebe parâmetro desse tipo` };
  }
  return null;
}
